/**
 * @file PickItemPickerDialog.tsx
 * @description Подбор позиции задания — кнопка «Подбор» в панели отметки
 * (phase19.24 этап 1, шаг 2: шторка справа, как «Подбор» в приёмке).
 *
 * Было модальное окно по центру, у остальных трёх документов — шторка справа
 * (`WmsSkuSelectDialog`). Кладовщик переключается между приёмкой и сборкой
 * десятки раз за смену, и одно и то же действие обязано открываться одинаково.
 * Поэтому здесь тот же `Sheet side="right"` и та же строка `SkuSelectRow`.
 *
 * Каталог не спрашиваем: состав задания фиксирован заказом, выбрать можно
 * только из его строк — иначе кладовщик отметит товар, которого в задании нет.
 *
 * Собранные и закрытые недостачей строки остаются видимыми, но недоступными и
 * серыми с подписью причины: пропавшая строка выглядит как сбой, а не как
 * «здесь всё сделано».
 *
 * Фокус после закрытия — общий порядок `usePickerFinalFocus`, тот же, что в
 * приёмке, списании и перемещении: выбрали строку → курсор в «Количество».
 */

import { Package, Search } from "lucide-react";
import { useEffect, useState, type RefObject } from "react";

import { NoticeStrip } from "../../ui/NoticeStrip";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../ui/sheet";
import { usePickerFinalFocus } from "../../ui/usePickerFinalFocus";
import { Input } from "../../ui/input";
import { SkuSelectRow } from "../SkuSelectRow";
import {
  formatPickItemName,
  isItemPicked,
  type PickingTaskItemDto,
} from "../../../lib/api-picking";
import { filterPickingItems } from "../../../lib/picking-search";
import { formatItemQty } from "../../../lib/picking-format";

interface Props {
  open: boolean;
  /** Все строки задания в порядке обхода склада. */
  items: PickingTaskItemDto[];
  onSelect: (item: PickingTaskItemDto) => void;
  /** Поле панели, куда уходит курсор после выбора строки («Количество»). */
  nextFieldRef?: RefObject<HTMLElement | null>;
  onClose: () => void;
}

/** Почему строка недоступна; `null` — строку можно выбрать. */
function lockedReason(item: PickingTaskItemDto): string | null {
  if (Number(item.shortageQty) > 0) return "закрыто недостачей";
  if (isItemPicked(item)) return "собрано полностью";
  return null;
}

export function PickItemPickerDialog({
  open,
  items,
  onSelect,
  nextFieldRef,
  onClose,
}: Props) {
  const focus = usePickerFinalFocus(nextFieldRef);
  const [query, setQuery] = useState("");
  const visible = filterPickingItems(items, query);
  const allDone = items.length > 0 && items.every((item) => lockedReason(item));

  // Сброс поиска при закрытии: следующий вызов открывается чистым.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <SheetContent
        side="right"
        finalFocus={focus.finalFocus}
        className="flex w-full flex-col gap-0 p-0 sm:max-w-lg"
      >
        <SheetHeader className="shrink-0 border-b border-on-surface/8 px-4 py-3">
          <SheetTitle>Выбрать позицию задания</SheetTitle>
          <SheetDescription>Показаны только позиции этого задания</SheetDescription>
        </SheetHeader>

        <div className="shrink-0 border-b border-on-surface/8 px-4 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-on-surface-variant" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Название или артикул"
              aria-label="Поиск по позициям задания"
              className="min-h-[56px] pl-9 text-base"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2">
          {allDone ? (
            <NoticeStrip tone="info" className="mx-2 mb-2">
              Все позиции задания собраны — нажмите «Провести сборку».
            </NoticeStrip>
          ) : null}

          {visible.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-on-surface-variant">
              <Package className="size-8" />
              {query.trim()
                ? `В задании нет позиций по запросу «${query.trim()}»`
                : "В задании нет позиций"}
            </div>
          ) : (
            <ul className="flex flex-col gap-1">
              {visible.map((item) => {
                const locked = lockedReason(item);
                return (
                  <SkuSelectRow
                    key={item.id}
                    title={formatPickItemName(item)}
                    code={item.skuCode}
                    note={formatItemQty(item.qtyPicked, item.qtyPlanned, item.unit)}
                    added={locked !== null}
                    addedText={locked ?? undefined}
                    onSelect={() => {
                      focus.markPicked();
                      onSelect(item);
                    }}
                  />
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
